import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";

const ThemeContext = createContext(null);

const getSystemTheme = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches ? "night" : "day";

const applyTheme = (theme) => {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme === "night" ? "dark" : "light";

  const themeColor = document.querySelector('meta[name="theme-color"]');
  if (themeColor) themeColor.content = theme === "night" ? "#121715" : "#f5f2ea";
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() =>
    document.documentElement.dataset.theme === "night" ? "night" : "day"
  );
  const [hasManualSelection, setHasManualSelection] = useState(false);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const updateFromSystem = (event) => {
      if (!hasManualSelection) setTheme(event.matches ? "night" : "day");
    };
    media.addEventListener("change", updateFromSystem);
    return () => media.removeEventListener("change", updateFromSystem);
  }, [hasManualSelection]);

  const value = useMemo(
    () => ({
      theme,
      toggleTheme: () => {
        const nextTheme = theme === "night" ? "day" : "night";
        const updateTheme = () => {
          setHasManualSelection(true);
          applyTheme(nextTheme);
          setTheme(nextTheme);
        };

        if (
          window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
          typeof document.startViewTransition !== "function"
        ) {
          updateTheme();
          return;
        }

        document.startViewTransition(() => flushSync(updateTheme));
      },
    }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};
